import React, {FC, memo, useCallback, useEffect, useRef, useState} from "react";
import {GridAreaContentLayout} from "../content/GridAreaContentLayout";
import './ChatContainer2.css'
import {GridAreaContentLayoutButton} from "../content/GridAreaContentLayoutButton";
import {useStateValue} from "../state-provider/StateProvider";
import {LiveChatHistory, LiveChatHistoryItem} from "../../api/types";
import {isEmpty, isEqual} from "lodash";
import {ws} from "../../events/ws";
import {liveChatAPI} from "../../api/liveChatAPI";
import {addMessageToLiveChat, setLiveChatHistory} from "./Chat.action";
import {log} from "../../utils/log";
import classnames from "classnames";
import {Virtuoso} from "react-virtuoso";
import {SingleComment2} from "./SingleComment2";
import {setGridAreaContent} from "../app/App.action";
import {useLiveChat} from "./useLiveChatProfile";
import { BsThreeDots } from "react-icons/bs";
import { LuCopy } from "react-icons/lu";
import {LuSendHorizonal} from "react-icons/lu";
import { BsChat } from "react-icons/bs";
import {useEventBus} from "../../hooks/useEventBus";
import {Events} from "../../utils/eventBus";

export const formatLabelLength = (label: string, maxLen = 50) => {
  if (label.length > maxLen) {
    return label.substring(0, maxLen) + "..."
  } else {
    return label
  }
}

export const ChatContainer2Component: FC<{
  organizationId: string
  experienceId: string
  referenceId: string
}> = ({
  organizationId,
  experienceId,
  referenceId
}) => {

  const [isHistoryLoading, setHistoryLoader] = React.useState(false)
  const [chatHistory, setChatHistory] = React.useState<LiveChatHistory>()

  const [lastLiveChatMessage, setLastLiveChatMessage] = useState<LiveChatHistoryItem>()
  const divRef = useRef<any>()

  const liveChat = useLiveChat(organizationId, referenceId)

  const [message, setMessage] = useState<string>()

  const [virtualTableKey, setVirtualTableKey] = useState("id")
  const [copiedLabel, setCopiedLabel] = useState<string>("")

  useEventBus(Events.LiveChatUpdated, e => {
    if (e.referenceId == referenceId) {
      getChatHistory()
    }
  })

  const [{ liveChatHistories, gridAreaContent, liveChatMode, promotionalDiscountCodes, translations }, dispatch] =
    useStateValue()

  React.useEffect(() => {
    if (liveChatHistories && referenceId) {
      let history = liveChatHistories.find(
        (h: LiveChatHistory) => h.referenceId === referenceId,
      )

      if (!isEqual(chatHistory, history)) {
        setChatHistory(history)
        if (history) {
          let historyLength = history.history.length
          if (historyLength > 0) {

            let kudosMessage: LiveChatHistoryItem | null = null
            for (var i = 0; i < 10; i++) {
              let comment = history.history[historyLength - 1 - i]
              if (comment?.replies?.find(r => r.type === "Kudos")) {
                kudosMessage = comment
                break
              }
            }
            if (kudosMessage && virtualTableKey != kudosMessage.id) {
              setVirtualTableKey(kudosMessage.id)
              console.log("RELOAD")
            }

            for (var i = historyLength - 1; i >= 0; i--) {
              let msg = history.history[i]
              if (msg && msg.id !== 'PENDING') {
                setLastLiveChatMessage(msg)
                break
              }
            }
          }
        } else {
          setLastLiveChatMessage(undefined)
        }
      }
    }
  }, [referenceId, liveChatHistories, divRef])

  const getChatHistory = async () => {
    let hash = Math.floor(new Date().getTime() / 1000).toString()
    if (!isHistoryLoading && isEmpty(chatHistory)) {
      setHistoryLoader(true)
    }

    try {
      let res = await liveChatAPI.getHistory(
        organizationId,
        experienceId,
        referenceId,
        hash,
        lastLiveChatMessage?.id ?? null,
      )
      if (lastLiveChatMessage && (!chatHistory?.history || chatHistory.referenceId === referenceId)) { // it is possible that chatHistory and res.history contain different histories for different chats when moving from one card to another
        const newIds = new Set(res.history.map(m => m.id))
        res = {
          ...res,
          history: (chatHistory?.history?.filter(m => m.id !== 'PENDING' && !newIds.has(m.id)) ?? []).concat(res.history)
        }
      }
      dispatch(setLiveChatHistory({ history: res }))
    } catch (e) {
      log(`Error getting chat history`, e)
    } finally {
      setHistoryLoader(false)
    }
  }

  useEffect(() => {
    const interval = setInterval(() => {
      if (liveChatMode !== 'RT') {
        getChatHistory()
      }
    }, 1000)

    return () => clearInterval(interval)
  }, [organizationId, referenceId, liveChat, liveChatMode, lastLiveChatMessage, chatHistory])

  useEffect(() => {
    if (organizationId && referenceId && liveChatMode === 'RT') {
      getChatHistory()
    }
  }, [liveChat, organizationId, referenceId, liveChatMode])

  useEffect(() => {
    if (divRef.current && !isEmpty(chatHistory) && (gridAreaContent === 'LiveChat')) {
      divRef.current.scrollToIndex(chatHistory!!.history.length - 1)
    }
  }, [gridAreaContent])

  const joinChat = () => {
    dispatch(setGridAreaContent('LiveChatSettings'))
  }

  const sendMessage = () => {
    if (message && message.trim().length > 0) {
      try {
        dispatch(addMessageToLiveChat({
          createdAt: (new Date().getTime() / 1000).toString(),
          experienceIds: [experienceId],
          id: 'PENDING',
          liveChatId: liveChat!!.liveChatId,
          referenceId: referenceId,
          sessionId: undefined,
          user: liveChat!!.profile,
          message: message,
          messageType: 'Message',
        }))
        setMessage('')
        ws.sendLiveChatMessage(
          organizationId,
          referenceId,
          message,
        )
      } catch (e) {
        log(`Error sending chat message`, e)
      }
    }
  }

  const renderItem = useCallback((index: number, comment: LiveChatHistoryItem | undefined) => {

    let commentType = comment?.type ?? "Message"
    let kudosComment = commentType === "Kudos"
    let isReplyToCurrentUser = Boolean(liveChat && comment && comment.replies && comment.replies.length > 0 && comment?.user.id === liveChat.profile.id)
    return (
      <>
        { comment && (
          <div key={comment.id} className={classnames({
            "chat-container2-chat-message-highlight": kudosComment || isReplyToCurrentUser,
          })}>
            <SingleComment2
              key={`comment-${comment.id}`}
              createdAt={parseInt(comment.createdAt)}
              message={commentType == "Kudos" ? "" : comment.message}
              reply={false}
              picture={comment.user.picture}
              name={commentType == "Kudos" ? `${comment.user.name} ${translations['LiveChatAnnouncingWinnerLabel'] || "announcing the winner!"}` : comment.user.name }
              type={commentType}
              showCreatedAt={commentType !== "Kudos"}
            />
            {kudosComment ? (
              <div className={"chat-container2-kudos-winner-message"}>
                <div className={"chat-container2-kudos-winner-message-line1"}>
                  🎉 {formatLabelLength(comment.message, 20)} 🎉
                </div>
                <div className={"chat-container2-kudos-winner-message-line2"}>
                  {translations['LiveChatWinnerContactedLabel'] || "Winner will be contacted via email"}
                </div>
              </div>
            ) : (comment.replies && comment.replies.map(
              (reply: LiveChatHistoryItem) => (
                <SingleComment2
                  key={`reply-${reply.id}`}
                  createdAt={parseInt(reply.createdAt)}
                  message={reply.message}
                  reply
                  picture={reply.user.picture}
                  name={reply.user.name}
                  type={reply.type}
                />
              ),
            ))}
          </div>
        )}
      </>
    )
  }, [liveChat])

  const onKeyDownSendMessage = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.code === 'Enter' || e.code === 'NumpadEnter') {
      sendMessage()
    }
  }

  return (
    <GridAreaContentLayout
      fullHeight={true}
      compact={true}
      contentNoVerticalPadding={true}
      heading={translations['LiveChatTitle'] || "comments"}
      footer={(
        <>
          <div className={"chat-container-footer"}>
            { liveChat ? (
              <div className={"chat-container-footer-controls"}>
                <button onClick={joinChat} className={"chat-container-footer-controls-settings"}><BsThreeDots/></button>
                <input
                  value={message}
                  className={"chat-container-footer-controls-message grid-area-content-layout-text-field"}
                  placeholder={translations['LiveChatInputMessageLabel'] || "Add a comment"}
                  onChange={e => setMessage(e.target.value)}
                  onKeyDown={onKeyDownSendMessage}
                />
                <button className={classnames("chat-container-footer-controls-send", { "disabled": !message })} onClick={sendMessage} disabled={Boolean(!message)}><LuSendHorizonal/></button>
              </div>
            ) : (
              <GridAreaContentLayoutButton onClick={joinChat} label={translations['LiveChatButtonJoinLabel'] || "Join the chat"} icon={<BsChat/>}/>
            )}
          </div>
        </>
      )}
    >
      <div className={"chat-container-layout"}>
        { promotionalDiscountCodes && promotionalDiscountCodes.length > 0 && (
          <div className={"chat-container-promotional-discount-codes"}>
            <div className={"chat-container-promotional-discount-codes-heading"}>
              {translations['PromotionalDiscountCodesHeading']}
            </div>
            {promotionalDiscountCodes.map(code => (
              <div key={code} className={"chat-container-promotional-discount-code"} onClick={() => {
                navigator.clipboard.writeText(code).then(
                  () => {
                    setCopiedLabel(translations['PromotionalDiscountCodeCopiedLabel'])
                    setTimeout(() => setCopiedLabel(""), 1200)
                  },
                  () => log("cannot copy url")
                );
              }}>
                <span className={"chat-container-promotional-discount-code-text"}>
                  {code}
                </span>
                <LuCopy className="icon-aligned-middle"/>
              </div>
            ))}
            {copiedLabel && (
              <div className="chat-container-promotional-discount-code-copied-label">
                {copiedLabel}
              </div>
            )}
          </div>
        )}
        <div className={"chat-container-content"}>
          { isHistoryLoading ? (
            <div className="chat-container-loader-container">
              <div className="circle-loader"/>
            </div>
            ) : (
            <Virtuoso
              key={"virtual-table-key-" + virtualTableKey}
              className={classnames('chat-container-2-chat-messages', {})}
              itemContent={renderItem}
              ref={divRef}
              data={chatHistory?.history}
              followOutput={'auto'}
              initialTopMostItemIndex={(chatHistory?.history?.length ?? 0) - 1}
            />
          )}
        </div>
      </div>
    </GridAreaContentLayout>
  )
}

export const ChatContainer2 = memo(ChatContainer2Component)